import {action, computed, observable} from "mobx";
import RequestsProcessor from "@src/Tools/AxiosRequestsProcessor";
import { Toast } from "@src/Tools/Toast";
import { Values } from "@src/Tools/Values";
import { IStringDict } from "../Values/types";
import _ from "lodash";
import { IAuthStore, SignOut, PinSignIn, ISessionToken, SignIn, TwoFactorAuthRequest, SetupCode2FA, ISignInResult } from "./types";

import Stores from "..";
import { IApiResponse } from "../../Interfaces/Entity/ApiResponse";
import { PostOAuthProviderForm, PostOAuthSignInByPinForm, PostOAuthSignInForm } from "../../Tools/OAuthRequestFormHelper";
import { IAuthByPinRequestOptions, IAuthByProviderRequestOptions, IAuthRequestOptions } from "../../Interfaces/AuthRequest";
const toast: Toast = new Toast();
const requestsProcessor: RequestsProcessor = new RequestsProcessor();

class AuthStore implements IAuthStore {
  @observable user: ISessionToken = null;
  @observable isAuthorized: boolean = false;
  @observable isUserTwoFactorAuthenticated: boolean = false;
  @observable isTwoFactorAuthenticationRequired: boolean = false;
  @observable isTwoFactorAuthenticationEnabledForUser: boolean = false;
  @observable backUrl: string = "";
  @observable userLocation: string = "";
  @observable afterAuthRedirectUrl: string = null;
  @observable redirectAfterLogin: string = null;
  @observable personId: string = "";

@computed get isAdmin() {
  if (this.user.roles?.includes(Stores.values.roles.Administrator)) {
      return true;
  } else {
      return false;
  }
}

  @action.bound
  async getUserDetails() {
    try {
      const response: ISessionToken = await requestsProcessor.get<ISessionToken>(
        "Authentication/GetCurrentSession",
        "UserDetails"
      );
      if (response) {
        this.user = response;
        this.isAuthorized = true;
      } else {
        this.isAuthorized = false;
      }
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async signIn(login: string, password: string, locationId: string, tenant?: string, returnUrl?: string): Promise<ISignInResult> {
    try {
      const signInPayload: SignIn = new SignIn();
      signInPayload.location = locationId || Stores.values.location;
      signInPayload.name = login;
      signInPayload.password = password;
      signInPayload.returnUrl = returnUrl;

      let requestUrl = "Authentication/SignIn";
      if (tenant) {
        requestUrl += `?tenant=${tenant}`;
      }
        const response: ISignInResult = await requestsProcessor.post<ISignInResult>(requestUrl, "SignIn", signInPayload);

      if (response) {
        this.isAuthorized = true;
        this.personId = response.personId;
        this.isTwoFactorAuthenticationEnabledForUser = response.isTwoFactorAuthenticationEnabledForUser;
        this.isTwoFactorAuthenticationRequired = response.isTwoFactorAuthenticationRequired;
        return response;
      }
      this.isAuthorized = false;
      return null;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async signInByPin(pin: string, locationId: string, tenant?: string) {
    try {
      const signInPayload: PinSignIn = new PinSignIn();
      signInPayload.pin = pin;
      signInPayload.location = locationId;

      let customErrorCaught = false;
      let customErrorProcessor = (error: any) => {
        if (error && error.response && error.response.status === 405) {
          let errorMessage: string = Values.getLocalizationValue("quickLoginLimit");
          toast.showError(errorMessage);
          customErrorCaught = true;
          return true;
        }
        return false;
      };

      let requestUrl = "Authentication/PinSignIn";
      if (tenant) {
        requestUrl += `?tenant=${tenant}`;
      }
      const response: ISessionToken = await requestsProcessor.post<ISessionToken>(
        requestUrl,
        "SignInByPin",
        signInPayload,
        true,
        customErrorProcessor
      );

      if (response) {
        this.user = response;
        this.isAuthorized = true;
        return true;
      }
      this.isAuthorized = false;
      return false;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async signInWith2FA(otpCode: string, tenant?: string, returnUrl?: string) {
    try {
      let requestUrl = "Authentication/SignInWithTwoFactorAuthentication";
      if (tenant) {
        requestUrl += `?tenant=${tenant}`;
        }
      const twoFactorAuthRequest: TwoFactorAuthRequest = new TwoFactorAuthRequest();
      twoFactorAuthRequest.otpCode = otpCode;
      twoFactorAuthRequest.returnUrl = returnUrl;
      const response: ISignInResult = await requestsProcessor.post<ISignInResult>(requestUrl, "SignInWithTwoFactorAuthentication", twoFactorAuthRequest);
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  signInViaOAuth2(options: IAuthRequestOptions, queryParams: string, shouldLinkAccounts?: boolean) {
    try {
        PostOAuthSignInForm(options, queryParams, shouldLinkAccounts);
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  signInViaOAuth2ByPin(options: IAuthByPinRequestOptions, queryParams: string) {
      try {
          PostOAuthSignInByPinForm(options, queryParams);
      } catch (error) {
          console.error(error);
      }
  }
  @action.bound
  signInViaOAuth2ByProvider(queryParams: string,options: IAuthByProviderRequestOptions) {
      try {
          PostOAuthProviderForm(queryParams, options);
      } catch (error) {
          console.error(error);
      }
  }
  @action.bound
  async signInViaExternalProvider(
    oAuthProviderId: string,
    locationId: string,
    ssoPostBack: string,
    ssoPostBackMode: string,
    ssoPostBackHash: string,
    associatedPersonId?: string,
    tenant?: string,
    afterAuthRedirectUrl?: string
  ) {
    try {
      let requestUrl = `Auth/SignIn?oAuthProviderId=${oAuthProviderId}&locationId=${locationId}&afterAuthRedirectUrl=${encodeURIComponent(afterAuthRedirectUrl)}&ssoPostBack=${ssoPostBack}&ssoPostBackMode=${ssoPostBackMode}&ssoPostBackHash=${ssoPostBackHash.replace(
        "#",
        ""
      )}&associatedPersonId=${associatedPersonId}`;
      if (tenant) {
        requestUrl += `&tenant=${tenant}`;
      }

      window.location.href = requestUrl;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getPin() {
    try {
        const response: IApiResponse<string> = await requestsProcessor.get<IApiResponse<string>>("persons/pin", "GetPin");
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async regeneratePin() {
    try {
        const response: IApiResponse<string> = await requestsProcessor.get<IApiResponse<string>>("persons/pin/generate", "RegeneratePin");
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async logOut() {
    try {
      const response: SignOut = await requestsProcessor.get<SignOut>("Authentication/SignOut", "SignOut");
      this.isAuthorized = false;
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async checkAuth(tenant?: string) {
    try {
      let requestUrl = "Authentication/GetCurrentSession";
      if (tenant) {
        requestUrl += `?tenant=${tenant}`;
      }
      const response: ISessionToken = await requestsProcessor.get<ISessionToken>(
        requestUrl,
        "GetCurrentSession",
        false,
        (error: any) => {
          // This prevents endless redirect loop since this particular query is
          // being used as authtorization detection and 401 is expected behavior here
          return error && error.response && error.response.status === 401;
        }
      );
      if (response) {
        this.isAuthorized = true;
        this.user = response;
        this.userLocation = response.location.name;
        return true;
      } else {
        this.isAuthorized = false;
      }
      return false;
    } catch (error) {
      console.error(error.response);
    }
  }

  @action.bound
  async getToken() {
    try {
      const response: any = await requestsProcessor.get<any>("Authentication/GetToken", "GetToken");
      return response.value;
    } catch (error) {
      console.error(error.response);
    }
  }

  @action.bound
  async changeProfilePassword(password: string) {
    try {
      const response: IStringDict = await requestsProcessor.patch<IStringDict>(
        "persons/password",
        "ChangeProfilePassword",
        { id: this.user.person.id, value: password }
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  setBackUrl(url: string) {
    this.backUrl = url;
  }

  @action.bound
  setAfterAuthRedirectUrl(url: string) {
    this.afterAuthRedirectUrl = url;
  }

  @action.bound
  getPersonInitials(name: string): string {
    var trimmedName = _(name).toUpper().trim();
    if (_.isEmpty(trimmedName)) {
      return "";
    }

    if (trimmedName.length <= 3) {
      return trimmedName;
    }

    var parts = _(trimmedName)
      .split(" ")
      .filter((x) => !_.isEmpty(x))
      .value();
    return parts.length > 1
      ? _(parts[0]).take(1).join("") + _(parts[1]).take(2).join("")
      : _(parts[0]).take(3).join("");
  }

  @action.bound
  async get2FASetup() {
    try {
      return await requestsProcessor.get<SetupCode2FA>("2fa/enable", "Get2FASetup");
    } catch (error) {
        console.error(error.response);
    }
  }

  @action.bound
  async Is2FASetupRequired() {
    try {
      const response = await requestsProcessor.get<IApiResponse<boolean>>("Authentication/IsTwoFactorAuthSetupRequired", "IsTwoFactorAuthSetupRequired");
      return response.data;
    } catch (error) {
      console.error(error.response);
    }
  }

  @action.bound
  async enable2FA(otpCode: string) {
    try {
      const response = await requestsProcessor.post<{enabled: boolean}>("2fa/enable", "Enable2FA", {otpCode});
      this.user.person.twoFactorAuthenticationEnabled = response.enabled;
      return response.enabled;
    } catch (error) {
      console.error(error.response);
    }
  }

  @action.bound
  async disable2FA(otpCode: string) {
    try {
      const response = await requestsProcessor.post<{disabled: boolean}>("2fa/disable", "Disable2FA", {otpCode});
      this.user.person.twoFactorAuthenticationEnabled = !response.disabled;
      return response.disabled;
    } catch (error) {
      console.error(error.response);
    }
  }

  @action.bound
  async disablePerson2FA(id: string) {
    try {
      const response = await requestsProcessor.delete<{removed: boolean}>(`2fa/${id}`, "DisablePerson2FA");

      if (response.removed) {
        await Stores.management.getPersons();
      }

      return response.removed;
    } catch (error) {
      console.error(error.message);
    }
  }
}

export default AuthStore;
